import type {
	AppID,
	AsyncNubeStorage,
	NubeStorageEvent,
	NubeStorageEventData,
	NubeStorageGetResponseEventData,
	NubeStorageId,
	NubeStorageSetResponseEventData,
} from "@tiendanube/nube-sdk-internal-types";
import {
	dispatchInternalMessage,
	listenInternalMessageOnce,
} from "../../messages";

// Worker side of NubeStorage
export function createWorkerStorage(
	appId: AppID,
	storageId: NubeStorageId,
): AsyncNubeStorage {
	let __requestId = 0;

	function nextRequestId() {
		return __requestId++;
	}

	function buildPromise<T extends NubeStorageEventData, K>(
		eventId: NubeStorageEvent,
		requestId: number,
		transformation: (data: T) => K,
	): Promise<K> {
		return new Promise<K>((resolve, reject) => {
			listenInternalMessageOnce<T>(
				appId,
				eventId,
				(data) => data.storageId === storageId && data.requestId === requestId,
				(data) => resolve(transformation(data)),
			);
		});
	}

	const storage: AsyncNubeStorage = {
		getItem: (key: string): Promise<string | null> => {
			const requestId = nextRequestId();
			const promise = buildPromise<
				NubeStorageGetResponseEventData,
				NubeStorageGetResponseEventData["value"]
			>("internal:storage:get:response", requestId, (data) => data.value);
			dispatchInternalMessage(appId, "internal:storage:get", {
				storageId,
				requestId,
				key,
			});
			return promise;
		},
		setItem: (key: string, value: string, ttl?: number): Promise<void> => {
			const requestId = nextRequestId();
			const promise = buildPromise<NubeStorageSetResponseEventData, void>(
				"internal:storage:set:response",
				requestId,
				() => {},
			);
			dispatchInternalMessage(appId, "internal:storage:set", {
				storageId,
				requestId,
				key,
				value,
				ttl,
			});
			return promise;
		},
		removeItem: (key: string): Promise<void> => {
			const requestId = nextRequestId();
			const promise = buildPromise<NubeStorageSetResponseEventData, void>(
				"internal:storage:remove:response",
				requestId,
				() => {},
			);
			dispatchInternalMessage(appId, "internal:storage:remove", {
				storageId,
				requestId,
				key,
			});
			return promise;
		},
	};

	return storage;
}
